/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * BI typed DataView sonuçlarının güvenli KPI, tablo ve basit grafik sunumu.
 * Tek kaynak `qubepro-core` reposudur.
 */

import './SemanticDataView.css';

export type SemanticDataPresentation = 'KPI' | 'TABLE' | 'CHART';

export type SemanticDataSourceReference = {
  id: string;
  code: string;
  name: string;
  href: string;
};

export type SemanticDataViewColumn = {
  id: string;
  label: string;
  dataType: string;
  role: 'DIMENSION' | 'MEASURE';
  format?: string;
  unit?: string;
  currency?: string;
};

export type SemanticDataViewModel = {
  contractVersion: 1;
  shape: 'TABLE';
  columns: SemanticDataViewColumn[];
  rows: Array<Record<string, string | number | boolean | null>>;
  totals?: Record<string, string | number | boolean | null>;
  provenance: {
    appId: string;
    modelHash: string;
    snapshotHash: string;
    dataAsOf: string;
    executionPlane: 'MANAGED_REPORTING';
    telemetryId: string;
    truncated: boolean;
    totalRows?: number;
  };
};

export type SemanticVerifiedFact = {
  id: string;
  label: string;
  value: string | number | boolean | null;
  formattedValue: string;
};

export type SemanticVerifiedAnalysis = {
  summary: string;
  facts: SemanticVerifiedFact[];
  insights: Array<{ id: string; text: string; factIds: string[] }>;
  limitations: string[];
};

function isRecord(value: unknown): value is Record<string, any> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function safeScalar(value: unknown): string | number | boolean | null {
  return value == null || ['string', 'number', 'boolean'].includes(typeof value)
    ? value as string | number | boolean | null
    : null;
}

export function normalizeSemanticDataPresentation(value: unknown): SemanticDataPresentation {
  return value === 'KPI' || value === 'CHART' ? value : 'TABLE';
}

export function normalizeSemanticDataSources(value: unknown): SemanticDataSourceReference[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value.slice(0, 20).flatMap((raw) => {
    if (!isRecord(raw)) return [];
    const id = typeof raw.id === 'string' ? raw.id.trim() : '';
    const code = typeof raw.code === 'string' ? raw.code.trim().slice(0, 100) : '';
    const name = typeof raw.name === 'string' ? raw.name.trim().slice(0, 255) : '';
    const href = typeof raw.href === 'string' ? raw.href.trim() : '';
    if (!/^[1-9]\d*$/.test(id) || !name || href !== `/bi/app/${id}` || seen.has(id)) return [];
    seen.add(id);
    return [{ id, code: code || id, name, href }];
  });
}

export function SemanticDataSources({
  sources,
  className = '',
}: {
  sources: SemanticDataSourceReference[];
  className?: string;
}) {
  if (sources.length === 0) return null;
  return (
    <div className={`qp-semantic-data-sources ${className}`.trim()} aria-label="BI veri kaynakları">
      <span>Kaynak</span>
      {sources.map((source) => (
        <a key={source.id} href={source.href} title={`${source.name} BI App kaynağını aç`}>
          {source.name}
        </a>
      ))}
    </div>
  );
}

export function normalizeSemanticDataView(value: unknown): SemanticDataViewModel | undefined {
  if (!isRecord(value) || value.contractVersion !== 1 || value.shape !== 'TABLE') return undefined;
  if (!Array.isArray(value.columns) || !Array.isArray(value.rows)) return undefined;
  const columns = value.columns.slice(0, 20).flatMap((raw) => {
    if (!isRecord(raw)) return [];
    const id = typeof raw.id === 'string' ? raw.id.trim() : '';
    const label = typeof raw.label === 'string' ? raw.label.trim() : '';
    const role = raw.role === 'DIMENSION' || raw.role === 'MEASURE' ? raw.role : null;
    if (!id || !label || !role) return [];
    return [{
      id,
      label,
      role,
      dataType: String(raw.dataType || 'UNKNOWN'),
      format: typeof raw.format === 'string' ? raw.format.slice(0, 100) : undefined,
      unit: typeof raw.unit === 'string' ? raw.unit.slice(0, 32) : undefined,
      currency: typeof raw.currency === 'string' && /^[A-Z]{3}$/.test(raw.currency)
        ? raw.currency
        : undefined,
    }];
  });
  const provenance = isRecord(value.provenance) ? value.provenance : {};
  const requiredProvenance = ['appId', 'modelHash', 'snapshotHash', 'dataAsOf', 'telemetryId']
    .every((key) => typeof provenance[key] === 'string' && provenance[key].trim());
  const executionPlane = provenance.executionPlane === 'MANAGED_REPORTING'
    ? provenance.executionPlane
    : null;
  if (columns.length === 0 || !executionPlane || !requiredProvenance) {
    return undefined;
  }
  const rows = value.rows.slice(0, 1000).flatMap((raw) => {
    if (!isRecord(raw)) return [];
    return [Object.fromEntries(columns.map((column) => [column.id, safeScalar(raw[column.id])]))];
  });
  const totals = isRecord(value.totals)
    ? Object.fromEntries(columns.map((column) => [column.id, safeScalar(value.totals[column.id])]))
    : undefined;
  return {
    contractVersion: 1,
    shape: 'TABLE',
    columns,
    rows,
    totals,
    provenance: {
      appId: provenance.appId.trim().slice(0, 100),
      modelHash: provenance.modelHash.trim().slice(0, 128),
      snapshotHash: provenance.snapshotHash.trim().slice(0, 128),
      dataAsOf: provenance.dataAsOf.trim(),
      executionPlane,
      telemetryId: provenance.telemetryId.trim().slice(0, 160),
      truncated: provenance.truncated === true,
      totalRows: typeof provenance.totalRows === 'number' && Number.isSafeInteger(provenance.totalRows) && provenance.totalRows >= 0
        ? provenance.totalRows
        : undefined,
    },
  };
}

export function normalizeSemanticVerifiedAnalysis(value: unknown): SemanticVerifiedAnalysis | undefined {
  if (!isRecord(value)) return undefined;
  const summary = typeof value.summary === 'string' ? value.summary.trim().slice(0, 2_000) : '';
  const facts = Array.isArray(value.facts) ? value.facts.slice(0, 100).flatMap((raw) => {
    if (!isRecord(raw)) return [];
    const id = typeof raw.id === 'string' && /^[A-Za-z0-9._-]{1,120}$/.test(raw.id) ? raw.id : '';
    const label = typeof raw.label === 'string' ? raw.label.trim().slice(0, 200) : '';
    if (!id || !label) return [];
    const scalar = safeScalar(raw.value);
    return [{
      id,
      label,
      value: scalar,
      formattedValue: typeof raw.formattedValue === 'string'
        ? raw.formattedValue.trim().slice(0, 200)
        : String(scalar ?? '-'),
    }];
  }) : [];
  const factIds = new Set(facts.map((fact) => fact.id));
  const insights = Array.isArray(value.insights) ? value.insights.slice(0, 20).flatMap((raw) => {
    if (!isRecord(raw)) return [];
    const id = typeof raw.id === 'string' && /^[A-Za-z0-9._-]{1,120}$/.test(raw.id) ? raw.id : '';
    const text = typeof raw.text === 'string' ? raw.text.trim().slice(0, 1_000) : '';
    const refs = Array.isArray(raw.factIds)
      ? raw.factIds.map(String).filter((ref) => factIds.has(ref)).slice(0, 20)
      : [];
    return id && text && refs.length > 0 ? [{ id, text, factIds: refs }] : [];
  }) : [];
  const limitations = Array.isArray(value.limitations)
    ? value.limitations
        .filter((item): item is string => typeof item === 'string' && Boolean(item.trim()))
        .map((item) => item.trim().slice(0, 500))
        .slice(0, 20)
    : [];
  return summary || facts.length > 0 || insights.length > 0 || limitations.length > 0
    ? { summary, facts, insights, limitations }
    : undefined;
}

function formatValue(value: unknown, column?: SemanticDataViewColumn): string {
  if (value == null) return '-';
  if (typeof value !== 'number') {
    const text = String(value);
    const numericMeasure = column?.role === 'MEASURE' && /^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?$/.test(text);
    const suffix = numericMeasure ? column?.currency || column?.unit : undefined;
    return suffix ? `${text} ${suffix}` : text;
  }
  try {
    if (column?.currency) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: column.currency,
        maximumSignificantDigits: 17,
      }).format(value);
    }
  } catch {
    // Tanınmayan locale/para biriminde güvenli sayısal gösterime düş.
  }
  const formatted = new Intl.NumberFormat(undefined, { maximumSignificantDigits: 17 }).format(value);
  return column?.unit ? `${formatted} ${column.unit}` : formatted;
}

export function SemanticDataView({
  view,
  presentation = 'TABLE',
  analysis,
  className = '',
}: {
  view: SemanticDataViewModel;
  presentation?: SemanticDataPresentation;
  analysis?: SemanticVerifiedAnalysis;
  className?: string;
}) {
  // A KPI request with several measures must keep every value visible.
  const isKpi = view.columns.length === 1 && view.rows.length === 1 && view.columns[0].role === 'MEASURE';
  const dimension = view.columns.find((column) => column.role === 'DIMENSION');
  const measure = view.columns.find((column) => column.role === 'MEASURE');
  const chartRows = presentation === 'CHART' && dimension && measure
    ? view.rows.slice(0, 12).map((row) => ({
        label: formatValue(row[dimension.id], dimension),
        rawValue: row[measure.id],
        value: typeof row[measure.id] === 'number' ? row[measure.id] as number
          : typeof row[measure.id] === 'string' && /^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?$/.test(row[measure.id] as string) ? Number(row[measure.id]) : NaN,
      })).filter((row) => Number.isFinite(row.value))
    : [];
  const displayedRowCount = isKpi ? 1 : chartRows.length > 0 ? chartRows.length : Math.min(view.rows.length, 100);
  const maximum = chartRows.reduce((max, row) => Math.max(max, Math.abs(row.value)), 0);
  const dataAsOf = Number.isFinite(new Date(view.provenance.dataAsOf).getTime())
    ? new Date(view.provenance.dataAsOf).toLocaleString()
    : '';
  const firstDimension = view.columns.findIndex((column) => column.role === 'DIMENSION');

  return (
    <div className={`qp-semantic-data-view ${className}`.trim()} aria-label="BI semantik veri sonucu">
      {isKpi ? (
        <div className="qp-semantic-data-kpi">
          <span className="qp-semantic-data-kpi-label">{view.columns[0]?.label}</span>
          <strong className="qp-semantic-data-kpi-value">
            {formatValue(view.rows[0]?.[view.columns[0]?.id], view.columns[0])}
          </strong>
        </div>
      ) : chartRows.length > 0 ? (
        <div className="qp-semantic-data-chart" aria-label={`${measure?.label || 'Ölçü'} grafiği`}>
          {chartRows.map((row, index) => (
            <div className="qp-semantic-data-chart-row" key={`${row.label}-${index}`}>
              <span className="qp-semantic-data-chart-label" title={row.label}>{row.label}</span>
              <span className="qp-semantic-data-chart-track" aria-hidden="true">
                <span
                  className="qp-semantic-data-chart-bar"
                  style={{ width: `${maximum > 0 ? Math.max(1, Math.abs(row.value) / maximum * 100) : 0}%` }}
                />
              </span>
              <strong>{formatValue(row.rawValue, measure)}</strong>
            </div>
          ))}
        </div>
      ) : (
        <div className="qp-semantic-data-table-wrap">
          <table className="qp-semantic-data-table">
            <thead><tr>{view.columns.map((column) => <th key={column.id}>{column.label}</th>)}</tr></thead>
            <tbody>
              {view.rows.slice(0, 100).map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {view.columns.map((column) => <td key={column.id}>{formatValue(row[column.id], column)}</td>)}
                </tr>
              ))}
            </tbody>
            {view.totals ? (
              <tfoot><tr>{view.columns.map((column, index) => (
                <td key={column.id}>
                  {index === firstDimension && view.totals?.[column.id] == null
                    ? 'Toplam'
                    : formatValue(view.totals?.[column.id], column)}
                </td>
              ))}</tr></tfoot>
            ) : null}
          </table>
        </div>
      )}
      <div className="qp-semantic-data-meta">
        <span>{view.provenance.executionPlane}</span>
        {dataAsOf ? <span>{dataAsOf}</span> : null}
        {view.provenance.totalRows != null ? <span>{view.provenance.totalRows} toplam satır</span> : null}
        {view.provenance.truncated || displayedRowCount < view.rows.length ? <span>Sonuç kırpıldı ({displayedRowCount} / {view.rows.length})</span> : null}
        <span>Model: {view.provenance.modelHash.slice(0, 8)}</span>
        <span>Snapshot: {view.provenance.snapshotHash.slice(0, 8)}</span>
        <span>Telemetry: {view.provenance.telemetryId}</span>
      </div>
      {analysis?.insights.length || analysis?.limitations.length ? (
        <div className="qp-semantic-data-analysis">
          {analysis.insights.length > 0 ? (
            <div>
              <strong>Doğrulanmış içgörüler</strong>
              <ul>{analysis.insights.map((insight) => (
                <li key={insight.id} data-fact-ids={insight.factIds.join(' ')}>{insight.text}</li>
              ))}</ul>
            </div>
          ) : null}
          {analysis.limitations.length > 0 ? (
            <div className="qp-semantic-data-limitations">
              <strong>Kısıtlar</strong>
              <ul>{analysis.limitations.map((limitation, index) => <li key={`${limitation}-${index}`}>{limitation}</li>)}</ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
